import React from 'react'
import PropTypes from 'prop-types'
import { TagIcon, EyeOffIcon, AdjustmentsIcon } from '@heroicons/react/solid'
import Modal from '@/shared/Modal'
import DefaultButton from '@/shared/Buttons/Default'
import { colorClasses } from '@/pages/ManageProjects/BucketsManager'

const SAMPLE_BUCKETS = [
  { name: 'Top picks', color: 'green' },
  { name: 'Maybe', color: 'yellow' },
  { name: 'Maybe with caveat', color: 'orange' },
  { name: 'Audio quality issues', color: 'red' }
]

const FeatureRow = ({ icon: Icon, title, body }) => (
  <div className='flex items-start gap-3'>
    <div className='flex-shrink-0 w-9 h-9 rounded-full bg-cccblue/10 dark:bg-cccblue/20 flex items-center justify-center'>
      <Icon className='h-5 w-5 text-cccblue' />
    </div>
    <div className='flex-1'>
      <div className='font-semibold text-gray-900 dark:text-gray-100 text-sm'>{title}</div>
      <div className='text-sm text-gray-600 dark:text-gray-300'>{body}</div>
    </div>
  </div>
)

FeatureRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired
}

const BucketsIntroModal = ({ isOpen, onDismiss }) => {
  return (
    <Modal isOpen={isOpen} closeModal={onDismiss} title='Welcome to Buckets'>
      <div className='text-gray-900 dark:text-gray-100 space-y-5'>
        <div className='flex items-center gap-3'>
          <div className='flex-shrink-0 w-12 h-12 rounded-full bg-cccblue flex items-center justify-center shadow-md'>
            <TagIcon className='h-7 w-7 text-white' />
          </div>
          <div>
            <h2 className='text-xl font-bold leading-tight'>Welcome to Buckets</h2>
            <p className='text-sm text-gray-600 dark:text-gray-300'>Your private way to organize submissions.</p>
          </div>
        </div>

        <p className='text-sm text-gray-700 dark:text-gray-200'>
          Got hundreds of auditions for one role? Buckets let you sort them into your own categories so nothing gets lost. Make as many as you need.
        </p>

        <div className='bg-white/60 dark:bg-gray-800/40 rounded-lg p-3'>
          <div className='text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2'>For example</div>
          <div className='flex flex-wrap gap-2'>
            {SAMPLE_BUCKETS.map((b) => {
              const c = colorClasses(b.color)
              return (
                <span key={b.name} className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
                  {b.name}
                </span>
              )
            })}
          </div>
        </div>

        <div className='space-y-3'>
          <FeatureRow
            icon={AdjustmentsIcon}
            title='Different from review status'
            body="Buckets are your own labels. They don't shortlist, dismiss, or assign — those still work the same."
          />
          <FeatureRow
            icon={EyeOffIcon}
            title='Only you can see them'
            body='Submitters never know which bucket they’re in. Buckets are private to you, the project owner.'
          />
          <FeatureRow
            icon={TagIcon}
            title='Filter and sort later'
            body='Use the Filter Bucket dropdown to jump straight to a category when you’re ready to make decisions.'
          />
        </div>

        <div className='flex justify-end pt-2'>
          <DefaultButton onClick={onDismiss} text='Got it — let’s go' />
        </div>
      </div>
    </Modal>
  )
}

BucketsIntroModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired
}

export default BucketsIntroModal
